import { useState, useRef, ChangeEvent } from "react";
import { ArticleSchemaContent } from "../gateways/cms/article";
import { useFileDrop } from "../features/shared/useFileDrop";

// 添付資料タブと統一したドロップゾーンの見た目
// ボタンは点線枠の外に表示し、点線枠の幅は「掲載時間」セレクトと揃える
const dropZoneRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
};
const dropZoneBoxStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    width: "400px",
    boxSizing: "border-box",
    padding: "8px 16px",
    border: "2px dashed #c9cbd3",
    borderRadius: "4px",
    backgroundColor: "#fafafa",
    fontSize: "13px",
    color: "#666",
};
// ドラッグ中のドロップ対象を分かりやすくするための見た目
const dropZoneBoxDragOverStyle: React.CSSProperties = {
    ...dropZoneBoxStyle,
    borderColor: "#3a3e5d",
    backgroundColor: "#eceef2",
};
const dropZoneHint = "ファイルをドラッグ&ドロップ、または";
// 点線枠内に表示するファイル名（選択中はヒント文言の代わりに表示する）
const dropZoneTextStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
};
const dropZoneButtonStyle: React.CSSProperties = {
    display: "inline-block",
    boxSizing: "border-box",
    flexShrink: 0,
    width: "auto",
    height: "32px",
    lineHeight: "28px",
    padding: "0 20px",
    textAlign: "center",
    whiteSpace: "nowrap",
    border: "2px solid #c9cbd3",
    borderRadius: "2px",
    backgroundColor: "#fff",
    fontSize: "14px",
    fontWeight: "normal",
    cursor: "pointer",
};

interface Props {
    onChange: (value: Record<string, string>) => unknown;
    pendingFileKey?: string;
    onPendingFileChange?: (key: string, file: File | null) => unknown;
    content: ArticleSchemaContent;
    setDiskFree: any;
    setLimitSize: any;
    buttonLabel?: string;
}

interface MinimumProps {
    onChange: (value: Record<string, string>) => unknown;
    pendingFileKey?: string;
    onPendingFileChange?: (key: string, file: File | null) => unknown;
    addDuration: any;
    index: number;
    maxTime: number;
    duration: number;
    content: ArticleSchemaContent;
    setDiskFree: any;
    setLimitSize: any;
}

interface MinimumImageProps {
    onChange: (value: Record<string, string>) => unknown;
    addDuration: any;
    changeDuration: (value: string) => unknown;
    index: number;
    maxTime: number;
    duration: number;
    content: ArticleSchemaContent;
    setDiskFree: any;
    setLimitSize: any;
}

interface SignageProps {
    onChange: (value: Record<string, string>) => unknown;
    pendingFileKey?: string;
    onPendingFileChange?: (key: string, file: File | null) => unknown;
    addDuration: any;
    index: number;
    duration: number;
    content: ArticleSchemaContent;
    setDiskFree: any;
    setLimitSize: any;
}

interface SignageImageProps {
    onChange: (value: Record<string, string>) => unknown;
    addDuration: any;
    changeDuration: (value: string) => unknown;
    index: number;
    duration: number;
    content: ArticleSchemaContent;
    setDiskFree: any;
    setLimitSize: any;
}

interface SignageURLProps {
    onChange: (value: Record<string, string>) => unknown;
    addDuration: any;
    changeDuration: (value: string) => unknown;
    changeImage: (contentData:string, contentFile: string) => unknown;
    index: number;
    duration: number;
    imageName: string;
    imageData: string;
    url: string;
    content: ArticleSchemaContent;
    setDiskFree: any;
    setLimitSize: any;
}

interface SignageTextProps {
    onChange: (value: Record<string, string>) => unknown;
    addDuration: any;
    changeDuration: (value: string) => unknown;
    changeImage: (contentData:string, contentFile: string) => unknown;
    index: number;
    duration: number;
    imageName: string;
    imageData: string;
    text: string;
    content: ArticleSchemaContent;
    setDiskFree: any;
    setLimitSize: any;
}

interface imageSize {
    width: number;
    hight: number;
}

const durationOptions = ['5','6','8','10','12','15','20','25','30','35','40','45','50','55','60','120','180','240','300'];
const accept_image = 'image/gif, image/jpeg, image/png';
const accept_video = 'video/mp4';
const signageFileSize = 310;  // MB
const minimumFileSize = 310;  // MB
const limitImageSize = { width: 3840, hight: 3840 };  // Pixel

const checkiImageSize = async (file:File) => {
    return new Promise<imageSize>((resolve, reject) => {
        const img = new Image();
    
        img.onload = () => {
          const size:imageSize = {
            width: img.naturalWidth,
            hight: img.naturalHeight,
          };
    
          URL.revokeObjectURL(img.src);
          resolve(size);
        };
    
        img.onerror = (error) => {
          reject(error);
        };
    
        img.src = URL.createObjectURL(file);
    });
};

/*** アップロードされたファイルが許可された種類かどうかを確認する ***/
const validateFileType = (file: File, accept: string, typeLabel: string): boolean => {
    const acceptedTypes = accept.split(",").map((t) => t.trim());
    if (!acceptedTypes.includes(file.type)) {
        alert(
            "対応していないファイル形式です（" + typeLabel + "のみアップロードできます）\n" +
            "選択されたファイル: " + file.name + "（" + (file.type || "不明な形式") + "）"
        );
        return false;
    }
    return true;
};

export function ArticleSchemaItemFileContentControl({ onChange, pendingFileKey, onPendingFileChange, content, setDiskFree, setLimitSize, buttonLabel = "ファイルを選択" }: Props) {
    const [selectedFileName, setSelectedFileName] = useState("");

    const processFiles = async (files: FileList | null) => {
        if (!files || !files.length) {
            return;
        }
        for (let i = 0; i < files.length; i++) {
            // ファイルサイズがディスク制限を超えた場合
            if (files[i].size > setDiskFree(0)) {
                alert("ファイルサイズがアップロード容量上限を超えています");
            }
            // 総ファイルサイズが300MB制限を超えた場合
            else if (files[i].size > setLimitSize(0)) {
                alert("総ファイルサイズが上限の300MBを超えています");
            }
            else{
                setSelectedFileName(files[i].name);
                if (files[i].type.includes("video")) {
                    if (pendingFileKey) {
                        onPendingFileChange?.(pendingFileKey, files[i]);
                    }
                    onChange({
                        data: "",
                        filename: files[i].name,
                        mimetype: files[i].type,
                        size: String(files[i].size),
                    });
                    setDiskFree(files[i].size);
                    setLimitSize(files[i].size);
                } else {
                    if (pendingFileKey) {
                        onPendingFileChange?.(pendingFileKey, null);
                    }
                    const s = new FileReader();
                    s.onload = () => {
                        onChange({
                            data: String(s.result),
                            filename: files[i].name,
                            mimetype: files[i].type,
                        });
                    };
                    s.readAsDataURL(files[i]);
                    setDiskFree(files[i].size);
                    setLimitSize(files[i].size);
                }
            }
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => processFiles(e.currentTarget.files);
    const { isDragOver, dropHandlers } = useFileDrop(processFiles);

    return (
        <label style={dropZoneRowStyle} {...dropHandlers}>
            <span style={isDragOver ? dropZoneBoxDragOverStyle : dropZoneBoxStyle}>
                <span style={dropZoneTextStyle} title={selectedFileName || undefined}>{selectedFileName || dropZoneHint}</span>
            </span>
            <input type="file" onChange={handleChange} hidden />
            <b style={dropZoneButtonStyle}>{buttonLabel}</b>
        </label>
    );
}

/***
 * 回数保証の画像アップロード
 ***/
export function ArticleSchemaImageContentControl({ onChange, addDuration, changeDuration,
                                                    index, maxTime, duration, content, setDiskFree, setLimitSize }: MinimumImageProps) {
    const [durationTime, setDurationTime] = useState("");
    const [totalTime, setTotalTime] = useState(0);
    const [selectedFileName, setSelectedFileName] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    /*** 画像掲載時間設定 ***/
    const changeDurationTime = (durationTime: string) => {
        setDurationTime(durationTime);
        const total = addDuration(Number(durationTime), index);
        setTotalTime(Number(total));
        changeDuration(durationTime);
    }

    // 編集の場合
    if(durationTime === "" && duration > 0.0){
        setDurationTime(String(duration));
    }
    // 新規追加の場合
    else if(durationTime === "" && duration === 0.0){
        changeDurationTime("10");
    }

    /*** ファイルサイズ制限設定 ***/
    const maxFileMB = content.limit || minimumFileSize;
    const maxFileSize = maxFileMB * 1024 *1024;

    /*** ファイル選択 ***/
    const processFiles = async (files: FileList | null) => {
        // ファイル選択がキャンセルされた場合
        if (!files || !files.length || !inputRef.current?.files) {
            if (inputRef.current?.files) {
                inputRef.current.files = new DataTransfer().files;
                addDuration(0, index);
                setDurationTime("");
                setSelectedFileName("");
            }
            return;
        }

        for (let i = 0; i < files.length; i++) {
            // ファイル形式が画像以外の場合
            if (!validateFileType(files[i], accept_image, "画像")) {
                setDurationTime("");
                setSelectedFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
                continue;
            }

            const size = await checkiImageSize(files[i]);
            // 総掲載時間が掲載時間制限を超えた場合
            if(totalTime > maxTime){
                alert("総掲載時間=" + Math.ceil(totalTime) + "秒 制限="+maxTime+"秒を超えています");
                // ファイル情報をリセット
                setDurationTime("");
                setSelectedFileName("");
                if (inputRef.current?.files){
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // ファイルサイズが制限を超えた場合
            else if(files[i].size > maxFileSize) {
                alert("ファイルサイズが制限=" + maxFileMB + "MBを超えています");
                // ファイル情報をリセット
                setDurationTime("");
                setSelectedFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // ファイルサイズが制限を超えた場合
            else if (files[i].size > setDiskFree(0)) {
                alert("ファイルサイズがアップロード容量上限を超えています");
                // ファイル情報をリセット
                setDurationTime("");
                setSelectedFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // 総ファイルサイズが300MB制限を超えた場合
            else if (files[i].size > setLimitSize(0)) {
                alert("総ファイルサイズが上限の300MBを超えています");
                // ファイル情報をリセット
                setDurationTime("");
                setSelectedFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // 画像サイズがが制限を超えた場合
            else if(size.hight > limitImageSize.hight || size.width > limitImageSize.width) {
                alert("画像サイズが制限=" + limitImageSize.hight + "x"+  limitImageSize.width + " Pixelを超えています (" + size.hight + "x"+  size.width + ")");
                // ファイル情報をリセット
                setDurationTime("");
                setSelectedFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // 総掲載時間とファイルサイズが設定内の場合
            else{
                // リクエスト値の設定
                setSelectedFileName(files[i].name);
                const s = new FileReader();
                s.onload = () => {
                    onChange({
                        data: String(s.result),
                        filename: files[i].name,
                        mimetype: files[i].type,
                        duration: durationTime,
                        index: String(index),
                        hight: String(size.hight),
                        width: String(size.width),
                    });
                };
                s.readAsDataURL(files[i]);
                setDiskFree(files[i].size);
                setLimitSize(files[i].size);
            }
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => processFiles(e.currentTarget.files);
    const { isDragOver, dropHandlers } = useFileDrop(processFiles);

    return (
        <>
            <select
                value={durationTime}
                onChange={(e) => changeDurationTime(e.target.value)}
                defaultValue={"10"}
            >
                {durationOptions.map((durationOption) => (
                    <option value={durationOption}>掲載時間  {durationOption}秒</option>
                ))}
            </select>

            { durationTime  && (
                <label style={dropZoneRowStyle} {...dropHandlers}>
                    <span style={isDragOver ? dropZoneBoxDragOverStyle : dropZoneBoxStyle}>
                        <span style={dropZoneTextStyle} title={selectedFileName || undefined}>{selectedFileName || dropZoneHint}</span>
                    </span>
                    <input type="file" id="inputFile" onChange={handleChange} accept={accept_image} ref={inputRef} hidden />
                    <b style={dropZoneButtonStyle}>ファイル選択</b>
                </label>
            ) }
        </>
    );
}

/***
 * 回数保証の動画アップロード
 ***/
export function ArticleSchemaVideoContentControl({ onChange, pendingFileKey, onPendingFileChange, addDuration, index, maxTime, duration, content, setDiskFree, setLimitSize }: MinimumProps) {
    const [durationTime, setDurationTime] = useState(0);
    const [selectedFileName, setSelectedFileName] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    /*** 掲載時間設定 ***/
    if(durationTime === 0 && duration > 0){
        const numericDuration = Math.ceil(content.value.duration);
//        const numericDuration = parseInt(content.value.duration, 10);
        setDurationTime(numericDuration);
    }

    /*** ファイルサイズ制限設定 ***/
    const maxFileMB = content.limit || minimumFileSize;
    const maxFileSize = maxFileMB * 1024 *1024;

    /*** ファイル選択 ***/
    const processFiles = (files: FileList | null) => {
        // ファイル選択がキャンセルされた場合
        if (!files || !files.length || !inputRef.current?.files) {
            if (inputRef.current?.files){
                inputRef.current.files = new DataTransfer().files;
                addDuration(0, index);
                setSelectedFileName("");
            }
            return;
        }
        for (let i = 0; i < files.length; i++) {
            // ファイル形式が動画以外の場合
            if (!validateFileType(files[i], accept_video, "動画")) {
                setSelectedFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
                continue;
            }

            // VIDEOタグの取得
            const video = document.createElement('video');
            const fileURL = URL.createObjectURL(files[i]);
            video.src = fileURL;
            // VIDEO再生時間の取得
            video.ondurationchange = async function () {
                //+1秒処理と表記用の表示時間も取得
                const unmodifiedDuration = video.duration;
                const numericalDuration = video.duration + 1;
                setDurationTime(numericalDuration);
//setDurationTime(unmodifiedDuration);
                const total = addDuration(numericalDuration, index);
                // VIDEO総掲載時間が掲載時間制限を超えた場合
                if(total > maxTime){
                    alert("総掲載時間=" + Math.ceil(total) + "秒 制限="+maxTime+"秒を超えています");
                    // ファイル情報をリセット
                    setDurationTime(0);
                    setSelectedFileName("");
                    if (inputRef.current?.files){
                        inputRef.current.files = new DataTransfer().files;
                        addDuration(0, index);
                    }
                // ファイルサイズが制限を超えた場合
                } else if (files[i].size > maxFileSize) {
                    alert("ファイルサイズが制限=" + maxFileMB + "MBを超えています");
                    // ファイル情報をリセット
                    setDurationTime(0);
                    setSelectedFileName("");
                    if (inputRef.current?.files) {
                        inputRef.current.files = new DataTransfer().files;
                        addDuration(0, index);
                    }
                }
                // ファイルサイズが制限を超えた場合
                else if (files[i].size > setDiskFree(0)) {
                    alert("ファイルサイズがアップロード容量上限を超えています");
                    // ファイル情報をリセット
                    setDurationTime(0);
                    setSelectedFileName("");
                    if (inputRef.current?.files) {
                        inputRef.current.files = new DataTransfer().files;
                        addDuration(0, index);
                    }
                }
                // 総ファイルサイズが300MB制限を超えた場合
                else if (files[i].size > setLimitSize(0)) {
                    alert("総ファイルサイズが上限の300MBを超えています");
                    // ファイル情報をリセット
                    setDurationTime(0);
                    setSelectedFileName("");
                    if (inputRef.current?.files) {
                        inputRef.current.files = new DataTransfer().files;
                        addDuration(0, index);
                    }
                }
                // 総掲載時間とファイルサイズが設定内の場合
                else{
                    // リクエスト値の設定
                    setSelectedFileName(files[i].name);
                    if (pendingFileKey) {
                        onPendingFileChange?.(pendingFileKey, files[i]);
                    }
                    onChange({
                        data: "",
                        filename: files[i].name,
                        mimetype: files[i].type,
                        size: String(files[i].size),
                        duration: String(numericalDuration),
                        index: String(index),
                    });
                    setDiskFree(files[i].size);
                    setLimitSize(files[i].size);
                }
            }
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => processFiles(e.currentTarget.files);
    const { isDragOver, dropHandlers } = useFileDrop(processFiles);

    return (
        <>
            <label style={dropZoneRowStyle} {...dropHandlers}>
                <span style={isDragOver ? dropZoneBoxDragOverStyle : dropZoneBoxStyle}>
                    <span style={dropZoneTextStyle} title={selectedFileName || undefined}>{selectedFileName || dropZoneHint}</span>
                </span>
                <input type="file" id="inputFile" onChange={handleChange} accept={accept_video} ref={inputRef} hidden />
                <b style={dropZoneButtonStyle}>ファイル選択</b>
            </label>
            { durationTime > 0 && ( <b>掲載時間 {Math.ceil(durationTime)} 秒</b>) }
        </>
    );
}

/***
 * サイネージ用の画像アップロード
 ***/
export function ArticleSignageImageContentControl({ onChange, addDuration, changeDuration,
                                                    index, duration, content, setDiskFree, setLimitSize }: SignageImageProps) {
    const [durationTime, setDurationTime] = useState("");
    const [selectedFileName, setSelectedFileName] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    /*** 画像掲載時間設定 ***/
    const changeDurationTime = (durationTime: string) => {
        setDurationTime(durationTime);
        addDuration(Number(durationTime), index);
        changeDuration(durationTime);
    }

    // 編集の場合
    if(durationTime === "" && duration > 0.0){
        setDurationTime(String(duration));
    }
    // 新規追加の場合
    else if(durationTime === "" && duration === 0.0){
        changeDurationTime("10");
    }

    /*** ファイルサイズ制限設定 ***/
    const maxFileMB = content.limit || signageFileSize;
    const maxFileSize = maxFileMB * 1024 *1024;

    /*** ファイル選択 ***/
    const processFiles = async (files: FileList | null) => {
        // ファイル選択がキャンセルされた場合
        if (!files || !files.length || !inputRef.current?.files) {
            if (inputRef.current?.files) {
                inputRef.current.files = new DataTransfer().files;
                addDuration(0, index);
                setDurationTime("");
                setSelectedFileName("");
            }
            return;
        }

        for (let i = 0; i < files.length; i++) {
            // ファイル形式が画像以外の場合
            if (!validateFileType(files[i], accept_image, "画像")) {
                setDurationTime("");
                setSelectedFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
                continue;
            }

            const size = await checkiImageSize(files[i]);
            // ファイルサイズが制限を超えた場合
            if (files[i].size > maxFileSize) {
                alert("ファイルサイズが制限=" + maxFileMB + "MBを超えています");
                // ファイル情報をリセット
                setDurationTime("");
                setSelectedFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // ファイルサイズが制限を超えた場合
            else if (files[i].size > setDiskFree(0)) {
                alert("ファイルサイズがアップロード容量上限を超えています");
                // ファイル情報をリセット
                setDurationTime("");
                setSelectedFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // 総ファイルサイズが300MB制限を超えた場合
            else if (files[i].size > setLimitSize(0)) {
                alert("総ファイルサイズが上限の300MBを超えています");
                // ファイル情報をリセット
                setDurationTime("");
                setSelectedFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // 画像サイズが制限を超えた場合
            else if(size.hight > limitImageSize.hight || size.width > limitImageSize.width) {
                alert("画像サイズが制限=" + limitImageSize.hight + "x"+  limitImageSize.width + " Pixelを超えています (" + size.hight + "x"+  size.width + ")");
                // ファイル情報をリセット
                setDurationTime("");
                setSelectedFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // ファイルサイズが設定内の場合
            else {
                // リクエスト値の設定
                setSelectedFileName(files[i].name);
                const s = new FileReader();
                s.onload = () => {
                    onChange({
                        data: String(s.result),
                        filename: files[i].name,
                        mimetype: files[i].type,
                        duration: durationTime,
                        index: String(index),
                        type: "signage-image",
                        hight: String(size.hight),
                        width: String(size.width),
                    });
                };
                s.readAsDataURL(files[i]);
                setDiskFree(files[i].size);
                setLimitSize(files[i].size);
            }
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => processFiles(e.currentTarget.files);
    const { isDragOver, dropHandlers } = useFileDrop(processFiles);

    return (
        <>
            <select
                value={durationTime}
                onChange={(e) => changeDurationTime(e.target.value)}
                defaultValue={"10"}
            >
                {durationOptions.map((durationOption) => (
                    <option value={durationOption}>掲載時間 {durationOption}秒</option>
                ))}
            </select>

            { durationTime  && (
                <label style={dropZoneRowStyle} {...dropHandlers}>
                    <span style={isDragOver ? dropZoneBoxDragOverStyle : dropZoneBoxStyle}>
                        <span style={dropZoneTextStyle} title={selectedFileName || undefined}>{selectedFileName || dropZoneHint}</span>
                    </span>
                    <input type="file" id="inputImage" onChange={handleChange} accept={accept_image} ref={inputRef} hidden />
                    <b style={dropZoneButtonStyle}>ファイル選択</b>
                </label>
            ) }
        </>
    );
}

/***
 * サイネージ用の動画アップロード
 ***/
export function ArticleSignageVideoContentControl({ onChange, pendingFileKey, onPendingFileChange, addDuration, index, duration, content, setDiskFree, setLimitSize }: SignageProps) {
    const [durationTime, setDurationTime] = useState(0);
    const [selectedFileName, setSelectedFileName] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    if(durationTime === 0 && duration > 0){
//        const numericDuration = parseInt(content.value.duration, 10);
        const numericDuration = Math.ceil(content.value.duration);
        setDurationTime(numericDuration);
    }

    /*** ファイルサイズ制限設定 ***/
    const maxFileMB = content.limit || signageFileSize;
    const maxFileSize = maxFileMB * 1024 *1024;

    /*** ファイル選択 ***/
    const processFiles = (files: FileList | null) => {
        // ファイル選択がキャンセルされた場合
        if (!files || !files.length || !inputRef.current?.files) {
            if (inputRef.current?.files){
                inputRef.current.files = new DataTransfer().files;
                addDuration(0, index);
                setSelectedFileName("");
            }
            return;
        }

        for (let i = 0; i < files.length; i++) {
            // ファイル形式が動画以外の場合
            if (!validateFileType(files[i], accept_video, "動画")) {
                setSelectedFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
                continue;
            }

            // VIDEOタグの取得
            const video = document.createElement('video');
            const fileURL = URL.createObjectURL(files[i]);
            video.src = fileURL;
            // VIDEO再生時間の取得
            video.ondurationchange = async function () {
                //+1秒処理と表記用の表示時間も取得
                const numericalDuration = video.duration + 1;
                setDurationTime(numericalDuration);
                addDuration(numericalDuration, index);
                // ファイルサイズが制限を超えた場合
                if (files[i].size > maxFileSize) {
                    alert("ファイルサイズが制限=" + maxFileMB + "MBを超えています");
                    // ファイル情報をリセット
                    setDurationTime(0);
                    setSelectedFileName("");
                    if (inputRef.current?.files) {
                        inputRef.current.files = new DataTransfer().files;
                        addDuration(0, index);
                    }
                }
                // ファイルサイズが制限を超えた場合
                else if (files[i].size > setDiskFree(0)) {
                    alert("ファイルサイズがアップロード容量上限を超えています");
                    // ファイル情報をリセット
                    setDurationTime(0);
                    setSelectedFileName("");
                    if (inputRef.current?.files) {
                        inputRef.current.files = new DataTransfer().files;
                        addDuration(0, index);
                    }
                }
                // 総ファイルサイズが300MB制限を超えた場合
                else if (files[i].size > setLimitSize(0)) {
                    alert("総ファイルサイズが上限の300MBを超えています");
                    // ファイル情報をリセット
                    setDurationTime(0);
                    setSelectedFileName("");
                    if (inputRef.current?.files) {
                        inputRef.current.files = new DataTransfer().files;
                        addDuration(0, index);
                    }
                }
                // ファイルサイズが設定内の場合
                else {
                    // リクエスト値の設定
                    setSelectedFileName(files[i].name);
                    if (pendingFileKey) {
                        onPendingFileChange?.(pendingFileKey, files[i]);
                    }
                    onChange({
                        data: "",
                        filename: files[i].name,
                        mimetype: files[i].type,
                        size: String(files[i].size),
                        duration: String(numericalDuration),
                        index: String(index),
                        type: "signage-video"
                    });
                    setDiskFree(files[i].size);
                    setLimitSize(files[i].size);
                }
            }
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => processFiles(e.currentTarget.files);
    const { isDragOver, dropHandlers } = useFileDrop(processFiles);

    return (
        <>
            <label style={dropZoneRowStyle} {...dropHandlers}>
                <span style={isDragOver ? dropZoneBoxDragOverStyle : dropZoneBoxStyle}>
                    <span style={dropZoneTextStyle} title={selectedFileName || undefined}>{selectedFileName || dropZoneHint}</span>
                </span>
                <input type="file" id="inputVideo" onChange={handleChange} accept={accept_video} ref={inputRef} hidden />
                <b style={dropZoneButtonStyle}>ファイル選択</b>
            </label>
            { durationTime > 0 && ( <b>掲載時間 {Math.ceil(durationTime)} 秒</b>) }
        </>
    );
}

/***
 * サイネージ用のURL設定
 ***/
export function ArticleSignageURLContentControl({ onChange, addDuration, changeDuration, changeImage,
                                                    index, duration, imageName, imageData, url, content, setDiskFree, setLimitSize }: SignageURLProps) {
    const [durationTime, setDurationTime] = useState("");
    const [subImage, setSubImage] = useState(imageData || "");
    const [fileName, setFileName] = useState(imageName || "");
    const [imageHight, setImageHight] = useState(0);
    const [imageWidth, setImageWidth] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    /*** URL掲載時間設定 ***/
    const changeDurationTime = (durationTime: string) => {
        setDurationTime(durationTime);
        addDuration(Number(durationTime), index);
        changeDuration(durationTime);
    }

    // 編集の場合
    if(durationTime === "" && duration > 0.0){
        setDurationTime(String(duration));
    }
    // 新規追加の場合
    else if(durationTime === "" && duration === 0.0){
        changeDurationTime("10");
    }

    /*** ファイルサイズ制限設定 ***/
    const maxFileMB = content.limit || signageFileSize;
    const maxFileSize = maxFileMB * 1024 *1024;

    /*** URLエラー時用画像設定 ***/
    const processSubImageFiles = async (files: FileList | null) => {
        // ファイル選択がキャンセルされた場合
        if (!files || !files.length) {
            setSubImage("");
            setFileName("");
            return;
        }

        for (let i = 0; i < files.length; i++) {
            // ファイル形式が画像以外の場合
            if (!validateFileType(files[i], accept_image, "画像")) {
                setDurationTime("");
                setFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
                continue;
            }

            const size = await checkiImageSize(files[i]);
            // ファイルサイズが制限を超えた場合
            if (files[i].size > maxFileSize) {
                alert("ファイルサイズが制限=" + maxFileMB + "MBを超えています");
                // ファイル情報をリセット
                setDurationTime("");
                setFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // ファイルサイズが制限を超えた場合
            else if (files[i].size > setDiskFree(0)) {
                alert("ファイルサイズがアップロード容量上限を超えています");
                // ファイル情報をリセット
                setDurationTime("");
                setFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // 総ファイルサイズが300MB制限を超えた場合
            else if (files[i].size > setLimitSize(0)) {
                alert("総ファイルサイズが上限の300MBを超えています");
                // ファイル情報をリセット
                setDurationTime("");
                setFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // 画像サイズが制限を超えた場合
            else if(size.hight > limitImageSize.hight || size.width > limitImageSize.width) {
                alert("画像サイズが制限=" + limitImageSize.hight + "x"+  limitImageSize.width + " Pixelを超えています (" + size.hight + "x"+  size.width + ")");
                // ファイル情報をリセット
                setDurationTime("");
                setFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // ファイルサイズが設定内の場合
            else {
                // 画像サイズ設定
                setImageHight(size.hight);
                setImageWidth(size.width);
                // リクエスト値の設定
                const s = new FileReader();
                s.onload = () => {
                    setSubImage(String(s.result));
                    setFileName(files[i].name);
                    // サムネイルの変更
                    changeImage(
                        String(s.result),
                        files[i].name,
                    );
                };
                onChange({
                    mimetype: "text/plain",
                    type: "signage-url",
                });
                s.readAsDataURL(files[i]);
                setDiskFree(files[i].size);
                setLimitSize(files[i].size);
            }
        }
    };

    /*** ファイル選択 ***/
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange({
            url: String(e.currentTarget.value),
            data: subImage,
            filename: fileName,
            mimetype: "text/plain",
            duration: durationTime,
            index: String(index),
            type: "signage-url",
            hight: String(imageHight),
            width: String(imageWidth),
        });
    }

    const changeSubImage = (e: ChangeEvent<HTMLInputElement>) => processSubImageFiles(e.currentTarget.files);
    const { isDragOver, dropHandlers } = useFileDrop(processSubImageFiles);

    return (
        <>
            <select
                value={durationTime}
                onChange={(e) => changeDurationTime(e.target.value)}
                defaultValue={"10"}
            >
                {durationOptions.map((durationOption) => (
                    <option value={durationOption}>掲載時間 {durationOption} 秒</option>
                ))}
            </select>

            { durationTime && (
                <label id='image' style={dropZoneRowStyle} {...dropHandlers}>
                    <span style={isDragOver ? dropZoneBoxDragOverStyle : dropZoneBoxStyle}>
                        <span style={dropZoneTextStyle} title={fileName || undefined}>{fileName || dropZoneHint}</span>
                    </span>
                    <input type="file" id="inputImage" onChange={changeSubImage} accept={accept_image} ref={inputRef} hidden />
                    <b style={dropZoneButtonStyle}>URLエラー時表示画像選択</b>
                </label>
            ) }
            { subImage  && (
                <label id='url'>
                    <input type="url" id="inputURL" onChange={handleChange} defaultValue={url} placeholder="URL入力" /><br/>
                </label>
            ) }
        </>
    );
}

/***
 * サイネージ用のテキスト設定
 ***/
export function ArticleSignageTextContentControl({ onChange, addDuration, changeDuration, changeImage,
                                                    index, duration, imageName, imageData, text, content, setDiskFree, setLimitSize }: SignageTextProps) {
    const [durationTime, setDurationTime] = useState("");
    const [subImage, setSubImage] = useState(imageData || "");
    const [fileName, setFileName] = useState(imageName || "");
    const [imageHight, setImageHight] = useState(0);
    const [imageWidth, setImageWidth] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    /*** テキスト掲載時間設定 ***/
    const changeDurationTime = (durationTime: string) => {
        setDurationTime(durationTime);
        addDuration(Number(durationTime), index);
        changeDuration(durationTime);
    }

    // 編集の場合
    if(durationTime === "" && duration > 0.0){
        setDurationTime(String(duration));
    }
    // 新規追加の場合
    else if(durationTime === "" && duration === 0.0){
        changeDurationTime("10");
    }

    /*** ファイルサイズ制限設定 ***/
    const maxFileMB = content.limit || signageFileSize;
    const maxFileSize = maxFileMB * 1024 *1024;

    /*** テキストエラー時用画像設定 ***/
    const processSubImageFiles = async (files: FileList | null) => {
        // ファイル選択がキャンセルされた場合
        if (!files || !files.length) {
            setSubImage("");
            setFileName("");
            return;
        }

        for (let i = 0; i < files.length; i++) {
            // ファイル形式が画像以外の場合
            if (!validateFileType(files[i], accept_image, "画像")) {
                setDurationTime("");
                setFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
                continue;
            }

            const size = await checkiImageSize(files[i]);
            // ファイルサイズが制限を超えた場合
            if (files[i].size > maxFileSize) {
                alert("ファイルサイズが制限=" + maxFileMB + "MBを超えています");
                // ファイル情報をリセット
                setDurationTime("");
                setFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // ファイルサイズが制限を超えた場合
            else if (files[i].size > setDiskFree(0)) {
                alert("ファイルサイズがアップロード容量上限を超えています");
                // ファイル情報をリセット
                setDurationTime("");
                setFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // 総ファイルサイズが300MB制限を超えた場合
            else if (files[i].size > setLimitSize(0)) {
                alert("総ファイルサイズが上限の300MBを超えています");
                // ファイル情報をリセット
                setDurationTime("");
                setFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // 画像サイズが制限を超えた場合
            else if(size.hight > limitImageSize.hight || size.width > limitImageSize.width) {
                alert("画像サイズが制限=" + limitImageSize.hight + "x"+  limitImageSize.width + " Pixelを超えています (" + size.hight + "x"+  size.width + ")");
                // ファイル情報をリセット
                setDurationTime("");
                setFileName("");
                if (inputRef.current?.files) {
                    inputRef.current.files = new DataTransfer().files;
                    addDuration(0, index);
                }
            }
            // ファイルサイズが設定内の場合
            else {
                // 画像サイズ設定
                setImageHight(size.hight);
                setImageWidth(size.width);
                // リクエスト値の設定
                const s = new FileReader();
                s.onload = () => {
                    setSubImage(String(s.result));
                    setFileName(files[i].name);
                    // サムネイルの変更
                    changeImage(
                        String(s.result),
                        files[i].name,
                    );
                };
                onChange({
                    mimetype: "text/plain",
                    type: "signage-text",
                    hight: String(size.hight),
                    width: String(size.width),
                });
                s.readAsDataURL(files[i]);
                setDiskFree(files[i].size);
                setLimitSize(files[i].size);
            }
        }
    };

    /*** ファイル選択 ***/
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange({
            text: String(e.currentTarget.value),
            data: subImage,
            filename: fileName,
            mimetype: "text/plain",
            duration: durationTime,
            index: String(index),
            type: "signage-text",
            hight: String(imageHight),
            width: String(imageWidth),
        });
    }
    /*** 文字数制限 ***/
    const limitTextLength = (e: ChangeEvent<HTMLInputElement>) => {
        let maxLength = 64; // 文字数の上限
        if (e.currentTarget.value.length > maxLength) {
            alert('文字数制限を超えました。');
            e.currentTarget.value = e.currentTarget.value.substring(0, maxLength);
        }
    }

    const changeSubImage = (e: ChangeEvent<HTMLInputElement>) => processSubImageFiles(e.currentTarget.files);
    const { isDragOver, dropHandlers } = useFileDrop(processSubImageFiles);

    return (
        <>
            <select
                value={durationTime}
                onChange={(e) => changeDurationTime(e.target.value)}
                defaultValue={"10"}
            >
                {durationOptions.map((durationOption) => (
                    <option value={durationOption}>掲載時間  {durationOption}秒</option>
                ))}
            </select>

            { durationTime && (
                <label id='image' style={dropZoneRowStyle} {...dropHandlers}>
                    <span style={isDragOver ? dropZoneBoxDragOverStyle : dropZoneBoxStyle}>
                        <span style={dropZoneTextStyle} title={fileName || undefined}>{fileName || dropZoneHint}</span>
                    </span>
                    <input type="file" id="inputImage" onChange={changeSubImage} accept={accept_image} ref={inputRef} hidden />
                    <b style={dropZoneButtonStyle}>代替画像選択</b>
                </label>
            ) }
            { subImage  && (
                <label id='text'>
                    <input type="text" id="inputText" maxLength={65} onInput={limitTextLength} onChange={handleChange} defaultValue={text} placeholder="文字列入力" /><br/>
                </label>
            ) }
        </>
    );
}
